import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FiEdit, FiTrash2, FiUpload, FiPlus, FiSearch, FiX, FiDownload } from 'react-icons/fi';
import { showNotification } from './Notification';
import ConfirmDialog from './ConfirmDialog';
import ProgressBar from './ProgressBar';
import ImportTraineesNew from './ImportTraineesNew';
import './Trainees.css';

// Collapses contiguous row numbers into ranges (e.g. 202,203,204 -> "202-204")
// so a block of identical errors reads as one line instead of a long list.
const formatRowRanges = (sortedRows) => {
  if (sortedRows.length === 0) return '';
  const ranges = [];
  let start = sortedRows[0];
  let end = sortedRows[0];
  for (let i = 1; i < sortedRows.length; i++) {
    if (sortedRows[i] === end + 1) {
      end = sortedRows[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = sortedRows[i];
      end = sortedRows[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);

  const label = ranges.length > 6
    ? `${ranges.slice(0, 6).join(', ')}, and ${ranges.length - 6} more range(s)`
    : ranges.join(', ');
  return `Row${sortedRows.length > 1 ? 's' : ''} ${label}`;
};

// Groups validation errors by their exact message combination, so hundreds of
// rows failing for the identical reason (e.g. blank placeholder rows) collapse
// into one summary line with a count and row range, instead of a wall of
// repetitive per-row entries that's easy to misread as "nothing happened".
const groupValidationErrors = (errors) => {
  const groups = new Map();
  errors.forEach((err) => {
    const messages = Array.isArray(err.errors) ? err.errors : [err.error];
    const key = messages.join(' + ');
    if (!groups.has(key)) groups.set(key, { message: key, rows: [] });
    groups.get(key).rows.push(err.row);
  });

  return Array.from(groups.values())
    .map((g) => {
      const rows = [...g.rows].sort((a, b) => a - b);
      return { message: g.message, count: rows.length, rangeLabel: formatRowRanges(rows) };
    })
    .sort((a, b) => b.count - a.count);
};

export { groupValidationErrors };

const Trainees = () => {
  const [trainees, setTrainees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [batches, setBatches] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [editingTrainee, setEditingTrainee] = useState(null);
  const [formData, setFormData] = useState({
    serial_number: '',
    tag_number: '',
    first_name: '',
    last_name: '',
    sex: '',
    date_of_birth: '',
    pwd: '',
    phone_number: '',
    national_id_number: '',
    bank_verification_number: '',
    account_number: '',
    bank_name: '',
    email: '',
    state_of_residence: '',
    lga_of_residence: '',
    parent_guardian_name: '',
    parent_guardian_address: '',
    parent_guardian_phone: '',
    education: '',
    employment_status: '',
    completion_status: '',
    specialization: '',
    status: 'active',
    batch_id: ''
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [uploading, setUploading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFields, setExportFields] = useState([]);
  const [operationProgress, setOperationProgress] = useState({ type: null, progress: 0, message: '' });
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [updatingId, setUpdatingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [noResultsMessage, setNoResultsMessage] = useState('');
  const [showUploadBatchModal, setShowUploadBatchModal] = useState(false);
  const [uploadBatchId, setUploadBatchId] = useState('');
  const [validationResults, setValidationResults] = useState(null);
  const [fileToken, setFileToken] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [detectedHeaders, setDetectedHeaders] = useState([]);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [columnMapping, setColumnMapping] = useState([]);
  const fileInputRef = useRef(null);
  const selectedBatchName = Array.isArray(batches) ? batches.find(batch => String(batch.id) === String(selectedBatchId))?.name : '';

  const handleDeleteBatchRecords = () => {
    if (!selectedBatchId) {
      showNotification('Please select a batch first', 'info');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Delete Batch Records',
      message: `This will permanently delete all trainees under ${selectedBatchName || 'the selected batch'}. This action cannot be undone.`,
      onConfirm: async () => {
        setOperationProgress({ type: 'batch-delete', progress: 20, message: 'Deleting batch records...' });
        try {
          await axios.delete(`/api/trainees/batch/${selectedBatchId}`);
          setOperationProgress({ type: 'batch-delete', progress: 100, message: 'Batch records deleted!' });
          setTimeout(() => {
            setOperationProgress({ type: null, progress: 0, message: '' });
            showNotification('Batch records deleted successfully', 'success');
            setSelectedIds([]);
            fetchTrainees();
          }, 600);
        } catch (error) {
          setOperationProgress({ type: null, progress: 0, message: '' });
          showNotification(error.response?.data?.error || 'Error deleting batch records', 'error');
        } finally {
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };


  useEffect(() => {
    fetchBatches();
    fetchTrainees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, searchTerm, selectedBatchId]);

  const fetchBatches = async () => {
    try {
      const response = await axios.get('/api/batches');
      setBatches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchTrainees = async () => {
    try {
      setLoading(true);
      const trimmedSearch = searchTerm.trim();
      const response = await axios.get('/api/trainees', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: trimmedSearch,
          batch_id: selectedBatchId || ''
        }
      });
      const fetchedTrainees = response.data.trainees;
      setTrainees(Array.isArray(fetchedTrainees) ? fetchedTrainees : []);
      setPagination(prev => ({
        ...prev,
        ...(response.data?.pagination || {})
      }));

      if (trimmedSearch && (!Array.isArray(fetchedTrainees) || fetchedTrainees.length === 0)) {
        const message = `No trainee found for "${trimmedSearch}".`;
        setNoResultsMessage(message);
        showNotification(message, 'info');
      } else {
        setNoResultsMessage('');
      }
    } catch (error) {
      console.error('Error fetching trainees:', error);
      showNotification('Error fetching trainees', 'error');
      setNoResultsMessage('');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (trainee) => {
    setEditingTrainee(trainee);
    setFormData({
      serial_number: trainee.serial_number || '',
      tag_number: trainee.tag_number || trainee.serial_number || '',
      first_name: trainee.first_name || '',
      last_name: trainee.last_name || '',
      sex: trainee.sex || '',
      date_of_birth: trainee.date_of_birth || '',
      pwd: trainee.pwd || '',
      phone_number: trainee.phone_number || '',
      national_id_number: trainee.national_id_number || '',
      bank_verification_number: trainee.bank_verification_number || '',
      account_number: trainee.account_number || '',
      bank_name: trainee.bank_name || '',
      email: trainee.email || '',
      state_of_residence: trainee.state_of_residence || '',
      lga_of_residence: trainee.lga_of_residence || '',
      parent_guardian_name: trainee.parent_guardian_name || '',
      parent_guardian_address: trainee.parent_guardian_address || '',
      parent_guardian_phone: trainee.parent_guardian_phone || '',
      education: trainee.education || '',
      employment_status: trainee.employment_status || '',
      completion_status: trainee.completion_status || '',
      specialization: trainee.specialization || '',
      status: trainee.status || 'active',
      batch_id: trainee.batch_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Trainee',
      message: 'Are you sure you want to delete this trainee? This action cannot be undone.',
      onConfirm: async () => {
        setDeletingIds(prev => new Set(prev).add(id));
        setOperationProgress({ type: 'delete', progress: 0, message: 'Deleting trainee...' });

        try {
          setOperationProgress(prev => ({ ...prev, progress: 50 }));
          await axios.delete(`/api/trainees/${id}`);
          setOperationProgress(prev => ({ ...prev, progress: 100, message: 'Trainee deleted successfully!' }));

          setTimeout(() => {
            setDeletingIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(id);
              return newSet;
            });
            setOperationProgress({ type: null, progress: 0, message: '' });
            showNotification('Trainee deleted successfully', 'success');
            fetchTrainees();
          }, 500);
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        } catch (error) {
          setDeletingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
          });
          setOperationProgress({ type: null, progress: 0, message: '' });
          showNotification('Error deleting trainee', 'error');
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Multiple Trainees',
      message: `Are you sure you want to delete ${selectedIds.length} trainee(s)? This action cannot be undone.`,
      onConfirm: async () => {
        setDeletingIds(new Set(selectedIds));
        setOperationProgress({ type: 'bulk-delete', progress: 0, message: `Deleting ${selectedIds.length} trainees...` });

        try {
          setOperationProgress(prev => ({ ...prev, progress: 30 }));
          await axios.post('/api/trainees/bulk-delete', { ids: selectedIds });
          setOperationProgress(prev => ({ ...prev, progress: 100, message: `${selectedIds.length} trainees deleted successfully!` }));

          setTimeout(() => {
            setDeletingIds(new Set());
            setSelectedIds([]);
            setOperationProgress({ type: null, progress: 0, message: '' });
            showNotification(`${selectedIds.length} trainees deleted successfully`, 'success');
            fetchTrainees();
          }, 500);
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        } catch (error) {
          setDeletingIds(new Set());
          setOperationProgress({ type: null, progress: 0, message: '' });
          showNotification('Error deleting trainees', 'error');
          setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdatingId(editingTrainee?.id || 'new');
    setOperationProgress({ type: editingTrainee ? 'update' : 'create', progress: 0, message: editingTrainee ? 'Updating trainee...' : 'Creating trainee...' });

    try {
      // Simulate progress
      setOperationProgress(prev => ({ ...prev, progress: 30 }));

      if (editingTrainee) {
        await axios.put(`/api/trainees/${editingTrainee.id}`, formData);
        setOperationProgress(prev => ({ ...prev, progress: 100, message: 'Trainee updated successfully!' }));
      } else {
        await axios.post('/api/trainees', formData);
        setOperationProgress(prev => ({ ...prev, progress: 100, message: 'Trainee created successfully!' }));
      }

      setTimeout(() => {
        setShowModal(false);
        setEditingTrainee(null);
        setUpdatingId(null);
        setOperationProgress({ type: null, progress: 0, message: '' });
        setFormData({
          serial_number: '',
          tag_number: '',
          first_name: '',
          last_name: '',
          sex: '',
          date_of_birth: '',
          pwd: '',
          phone_number: '',
          national_id_number: '',
          bank_verification_number: '',
          account_number: '',
          bank_name: '',
          email: '',
          state_of_residence: '',
          lga_of_residence: '',
          parent_guardian_name: '',
          parent_guardian_address: '',
          parent_guardian_phone: '',
          education: '',
          employment_status: '',
          completion_status: '',
          specialization: '',
          status: 'active',
          batch_id: ''
        });
        showNotification(editingTrainee ? 'Trainee updated successfully' : 'Trainee created successfully', 'success');
        fetchTrainees();
      }, 500);
    } catch (error) {
      setUpdatingId(null);
      setOperationProgress({ type: null, progress: 0, message: '' });
      showNotification(error.response?.data?.error || 'Error saving trainee', 'error');
    }
  };

  const handleUploadButtonClick = () => {
    if (batches.length === 0) {
      showNotification('Please create a batch first before importing trainees', 'info');
      return;
    }
    setShowUploadBatchModal(true);
  };

  const handleBatchSelectedForUpload = () => {
    if (!uploadBatchId) {
      showNotification('Please select a batch', 'error');
      return;
    }
    setShowUploadBatchModal(false);
    // Trigger file input
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setUploadBatchId('');
      return;
    }

    if (!uploadBatchId) {
      showNotification('Please select a batch for this import', 'error');
      e.target.value = '';
      return;
    }

    setUploading(true);
    setOperationProgress({ type: 'upload', progress: 0, message: 'Preparing import...' });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('batch_id', uploadBatchId);

    try {
      // Upload progress
      setOperationProgress(prev => ({ ...prev, progress: 10, message: 'Importing file for validation...' }));

      const response = await axios.post('/api/trainees/upload/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 minutes for slower DNS checks
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setOperationProgress(prev => ({
              ...prev,
              progress: Math.min(30, 10 + (percentCompleted * 0.2)),
              message: `Importing... ${percentCompleted}%`
            }));
          }
        }
      });

      setOperationProgress({ type: null, progress: 0, message: '' });

      // This first call never sends a mapping - it's purely detection: the
      // server reads the file's headers, suggests a mapping, and stores the
      // file for the next step. The admin confirms/adjusts that mapping
      // before anything is actually parsed or validated.
      const { needsMapping, fileToken: token, headers, suggestedMapping, fieldDefinitions: fields } = response.data;

      if (needsMapping) {
        setFileToken(token);
        setDetectedHeaders(headers || []);
        setFieldDefinitions(fields || []);
        setColumnMapping(suggestedMapping || []);
        setEditingTrainee(null); // Ensure we are NOT in edit mode
        setShowMappingModal(true);
      } else {
        showNotification('Unexpected response while reading the file.', 'error');
      }

    } catch (error) {
      setOperationProgress({ type: null, progress: 0, message: '' });
      console.error('[Frontend] File read failed:', error);
      console.error('[Frontend] Error details:', error.response?.data);

      const errorMessage = error.response?.data?.error || error.message || 'Error reading file';
      showNotification(errorMessage, 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleMappingCancel = () => {
    setShowMappingModal(false);
    setFileToken(null);
    setColumnMapping([]);
    setDetectedHeaders([]);
    setFieldDefinitions([]);
  };

  const handleMappingContinue = async () => {
    const requiredKeys = fieldDefinitions.filter(f => f.required).map(f => f.key);
    const mappedKeys = new Set(columnMapping.filter(Boolean));
    const missing = requiredKeys.filter(k => !mappedKeys.has(k));
    if (missing.length > 0) {
      const missingLabels = fieldDefinitions.filter(f => missing.includes(f.key)).map(f => f.label).join(', ');
      showNotification(`Please map these required fields before continuing: ${missingLabels}`, 'error');
      return;
    }

    setUploading(true);
    setOperationProgress({ type: 'upload', progress: 30, message: 'Validating emails and data...' });

    try {
      const formData = new FormData();
      formData.append('fileToken', fileToken);
      formData.append('batch_id', uploadBatchId);
      formData.append('columnMapping', JSON.stringify(columnMapping));

      const response = await axios.post('/api/trainees/upload/validate', formData, {
        timeout: 600000 // 10 minutes for slower DNS checks
      });

      setOperationProgress({ type: null, progress: 0, message: '' });

      const { isValid, errors, total } = response.data;
      setShowMappingModal(false);
      setValidationResults({ isValid, total, errors: errors || [] });
      setShowValidationModal(true);

    } catch (error) {
      setOperationProgress({ type: null, progress: 0, message: '' });
      console.error('[Frontend] Validation Request Failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error validating file';
      showNotification(errorMessage, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async (skipInvalidRows = false) => {
    if (!fileToken || !uploadBatchId) return;

    setUploading(true);
    setOperationProgress({ type: 'upload', progress: 0, message: skipInvalidRows ? 'Skipping invalid rows and importing the rest...' : 'Importing confirmed data...' });
    setShowValidationModal(false);

    try {
      setOperationProgress(prev => ({ ...prev, progress: 20, message: 'Starting import...' }));
      const response = await axios.post('/api/trainees/upload/confirm', {
        fileToken,
        batchId: uploadBatchId,
        columnMapping,
        skipInvalidRows
      });

      setOperationProgress(prev => ({ ...prev, progress: 100, message: 'Import successful!' }));

      setTimeout(() => {
        setOperationProgress({ type: null, progress: 0, message: '' });
        const needsAttention = (response.data.duplicates || 0) > 0 || (response.data.needsFix || []).length > 0;
        showNotification(
          response.data.message,
          needsAttention ? 'warning' : 'success',
          needsAttention ? 8000 : 3000
        );
        setFileToken(null);
        setValidationResults(null);
        setShowValidationModal(false);
        setShowModal(false); // Close the modal
        setUploadBatchId('');
        setColumnMapping([]);
        setDetectedHeaders([]);
        setFieldDefinitions([]);
        fetchTrainees();
      }, 1500);

    } catch (error) {
      setOperationProgress({ type: null, progress: 0, message: '' });
      showNotification(error.response?.data?.error || error.message || 'Import failed - please check your connection and try again', 'error', 8000);
    } finally {
      setUploading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (Array.isArray(trainees) && selectedIds.length === trainees.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(Array.isArray(trainees) ? trainees.map(t => t.id) : []);
    }
  };

  const allExportFields = [
    { key: 'serial_number', label: 'SN' },
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'sex', label: 'Sex' },
    { key: 'date_of_birth', label: 'Date of Birth (DOB)' },
    { key: 'pwd', label: 'PWD' },
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'national_id_number', label: 'National ID Number (NIN)' },
    { key: 'bank_verification_number', label: 'Bank Verification Number (BVN)' },
    { key: 'account_number', label: 'Account Number' },
    { key: 'bank_name', label: 'Bank Name' },
    { key: 'email', label: 'Email' },
    { key: 'state_of_residence', label: 'State of Residence' },
    { key: 'lga_of_residence', label: 'LGA of Residence' },
    { key: 'parent_guardian_name', label: 'Name of Trainee\'s Parent or Guardian' },
    { key: 'parent_guardian_address', label: 'Address of Trainee\'s Parent or Guardian' },
    { key: 'parent_guardian_phone', label: 'Phone Number of Trainee\'s Parent or Guardian' },
    { key: 'education', label: 'Education' },
    { key: 'employment_status', label: 'Employment Status' },
    { key: 'completion_status', label: 'Completion Status' },
    { key: 'specialization', label: 'Specialization' },
    { key: 'status', label: 'Status' }
  ];

  const handleExport = async (exportAll = false) => {
    setExporting(true);
    setOperationProgress({ type: 'export', progress: 0, message: 'Preparing export...' });

    try {
      setOperationProgress(prev => ({ ...prev, progress: 20, message: 'Fetching data...' }));
      const fields = exportAll ? 'all' : exportFields.join(',');

      setOperationProgress(prev => ({ ...prev, progress: 40, message: 'Generating Excel file...' }));
      const response = await axios.get('/api/trainees/export/excel', {
        params: { fields },
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setOperationProgress(prev => ({
              ...prev,
              progress: 40 + (percentCompleted * 0.5),
              message: `Downloading... ${percentCompleted}%`
            }));
          }
        }
      });

      setOperationProgress(prev => ({ ...prev, progress: 90, message: 'Finalizing download...' }));

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trainees_export_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setOperationProgress(prev => ({ ...prev, progress: 100, message: 'Export completed successfully!' }));

      setTimeout(() => {
        setOperationProgress({ type: null, progress: 0, message: '' });
        setExporting(false);
        showNotification('Export completed successfully', 'success');
        setShowExportModal(false);
        setExportFields([]);
      }, 1000);
    } catch (error) {
      setExporting(false);
      setOperationProgress({ type: null, progress: 0, message: '' });
      showNotification('Error exporting data', 'error');
    }
  };

  const toggleExportField = (fieldKey) => {
    setExportFields(prev =>
      prev.includes(fieldKey)
        ? prev.filter(f => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  return (
    <div className="trainees">
      {operationProgress.type && (
        <div className="operation-progress-overlay">
          <div className="operation-progress-card">
            <ProgressBar
              progress={operationProgress.progress}
              message={operationProgress.message}
            />
          </div>
        </div>
      )}

      <div className="trainees-header">
        <h1>Trainees Management</h1>
        <div className="trainees-actions">
          <ImportTraineesNew batches={batches} onImported={fetchTrainees} />
          {/* Old import flow kept fully intact (state/handlers/modals below) but
              hidden from the UI now that Import Trainees (New) is the primary
              path - flip this back to true instead of the "New" button if the
              new flow ever needs to be rolled back. */}
          <button
            className="btn btn-secondary"
            style={{ marginRight: '10px', display: 'none' }}
            onClick={handleUploadButtonClick}
            disabled={uploading}
          >
            <FiUpload /> {uploading ? 'Importing...' : 'Import CSV/Excel'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.ods"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            disabled={uploading}
          />
          <button
            className="btn btn-secondary"
            onClick={() => setShowExportModal(true)}
            style={{ marginRight: '10px' }}
            disabled={exporting}
          >
            <FiDownload /> {exporting ? 'Exporting...' : 'Export'}
          </button>
          <button className="btn btn-primary" onClick={() => {
            setEditingTrainee(null);
            setShowValidationModal(false);
            setValidationResults(null);
            setFormData({
              serial_number: '', tag_number: '', first_name: '', last_name: '', sex: '', date_of_birth: '', pwd: '',
              phone_number: '', national_id_number: '', bank_verification_number: '', email: '',
              state_of_residence: '', lga_of_residence: '', parent_guardian_name: '',
              parent_guardian_address: '', parent_guardian_phone: '', education: '',
              employment_status: '', completion_status: '', specialization: '', status: 'active', batch_id: ''
            });
            setShowModal(true);
          }}>
            <FiPlus /> Add Trainee
          </button>
        </div>
      </div>

      <div className="trainees-toolbar">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by name, SN, specialization..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
          />
        </div>
        <div className="batch-selector">
          <label>Filter by Batch:</label>
          <select
            value={selectedBatchId}
            onChange={(e) => {
              setSelectedBatchId(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="batch-select"
          >
            <option value="">All Batches</option>
            {Array.isArray(batches) && batches.map(batch => (
              <option key={batch.id} value={batch.id}>{batch.name}</option>
            ))}
          </select>
        </div>
        <button
          className="btn btn-danger"
          onClick={handleDeleteBatchRecords}
          disabled={!selectedBatchId || operationProgress.type === 'batch-delete'}
        >
          Delete Batch Records
        </button>
        {selectedIds.length > 0 && (
          <button className="btn btn-danger" onClick={handleBulkDelete}>
            <FiTrash2 /> Delete Selected ({selectedIds.length})
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading trainees...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedIds.length === trainees.length && trainees.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>SN</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Sex</th>
                  <th>DOB</th>
                  <th>PWD</th>
                  <th>Phone Number</th>
                  <th>NIN</th>
                  <th>BVN</th>
                  <th>Account Number</th>
                  <th>Bank Name</th>
                  <th>Email</th>
                  <th>State</th>
                  <th>LGA</th>
                  <th>Parent/Guardian Name</th>
                  <th>Parent/Guardian Address</th>
                  <th>Parent/Guardian Phone</th>
                  <th>Education</th>
                  <th>Employment Status</th>
                  <th>Completion Status</th>
                  <th>Specialization</th>
                  <th>Batch</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {trainees.length === 0 ? (
                  <tr>
                    <td colSpan="25" className="no-data">
                      {noResultsMessage || 'No trainees available yet. Please add or import trainee data.'}
                    </td>
                  </tr>
                ) : (
                  Array.isArray(trainees) && trainees.map(trainee => (
                    <tr key={trainee.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(trainee.id)}
                          onChange={() => toggleSelect(trainee.id)}
                        />
                      </td>
                      <td>{trainee.serial_number || '-'}</td>
                      <td>{trainee.first_name || '-'}</td>
                      <td>{trainee.last_name || '-'}</td>
                      <td>{trainee.sex || '-'}</td>
                      <td>{trainee.date_of_birth ? new Date(trainee.date_of_birth).toLocaleDateString() : '-'}</td>
                      <td>{trainee.pwd || '-'}</td>
                      <td>{trainee.phone_number || '-'}</td>
                      <td>{trainee.national_id_number || '-'}</td>
                      <td>{trainee.bank_verification_number || '-'}</td>
                      <td>{trainee.account_number || '-'}</td>
                      <td>{trainee.bank_name || '-'}</td>
                      <td>{trainee.email || '-'}</td>
                      <td>{trainee.state_of_residence || '-'}</td>
                      <td>{trainee.lga_of_residence || '-'}</td>
                      <td>{trainee.parent_guardian_name || '-'}</td>
                      <td className="text-truncate" title={trainee.parent_guardian_address || ''}>{trainee.parent_guardian_address ? (trainee.parent_guardian_address.length > 30 ? `${trainee.parent_guardian_address.substring(0, 30)}...` : trainee.parent_guardian_address) : '-'}</td>
                      <td>{trainee.parent_guardian_phone || '-'}</td>
                      <td>{trainee.education || '-'}</td>
                      <td>{trainee.employment_status || '-'}</td>
                      <td>{trainee.completion_status || '-'}</td>
                      <td>{trainee.specialization || '-'}</td>
                      <td>{trainee.batch_name || '-'}</td>
                      <td>
                        <span className={`status-badge ${trainee.status}`}>
                          {trainee.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-icon"
                          onClick={() => handleEdit(trainee)}
                          disabled={updatingId === trainee.id || deletingIds.has(trainee.id)}
                          title={updatingId === trainee.id ? 'Updating...' : 'Edit'}
                        >
                          <FiEdit />
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => handleDelete(trainee.id)}
                          disabled={deletingIds.has(trainee.id) || updatingId === trainee.id}
                          title={deletingIds.has(trainee.id) ? 'Deleting...' : 'Delete'}
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <span>Page {pagination.page} of {pagination.pages}</span>
              <button
                className="btn btn-secondary"
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="modal" onClick={() => { setShowModal(false); setShowValidationModal(false); setEditingTrainee(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showValidationModal ? 'Validation Report' : (editingTrainee ? 'Edit Trainee' : 'Add Trainee')}</h2>
              <button className="close-btn" onClick={() => { setShowModal(false); setShowValidationModal(false); setEditingTrainee(null); }}>
                <FiX />
              </button>
            </div>

            {/* Validation Report Modal */}
            {showValidationModal && validationResults && (
              <div style={{ padding: '20px' }}>
                {validationResults.isValid ? (
                  <div className="text-center">
                    <div className="alert alert-success" style={{ padding: '15px', marginBottom: '20px', borderRadius: '4px', backgroundColor: '#d4edda', color: '#155724' }}>
                      <h4>Validation Successful!</h4>
                      <p>All <strong>{validationResults.total}</strong> records passed validation checks.</p>
                      <p><small>Checked: Required Fields, Data Types, Email Formats, and Domain Existence.</small></p>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                      <button className="btn btn-secondary" onClick={() => { setShowValidationModal(false); setFileToken(null); }}>Cancel</button>
                      <button className="btn btn-primary" onClick={() => handleConfirmUpload(false)}>Confirm & Import</button>
                    </div>
                  </div>
                ) : (() => {
                  const validCount = validationResults.total - validationResults.errors.length;
                  const groups = groupValidationErrors(validationResults.errors);
                  const skippableCount = validationResults.errors.filter(e => e.skippable).length;
                  return (
                    <div>
                      <div className="alert alert-danger" style={{ padding: '15px', marginBottom: '20px', borderRadius: '4px', backgroundColor: '#f8d7da', color: '#721c24' }}>
                        <h4>Validation Failed</h4>
                        <p style={{ fontSize: '1.05em' }}>
                          <strong>{validCount}</strong> of <strong>{validationResults.total}</strong> rows are ready to import.{' '}
                          <strong>{validationResults.errors.length}</strong> rows have errors and will be skipped until fixed.
                        </p>
                        <p>Import is blocked until you either fix the file and re-import, or trim it down to just the valid rows.</p>
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <h5 style={{ margin: '0 0 10px' }}>Error patterns found</h5>
                        <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
                          {groups.map((g, idx) => (
                            <div key={idx} style={{ padding: '12px 15px', borderBottom: idx < groups.length - 1 ? '1px solid #dee2e6' : 'none', backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                <span className="text-danger" style={{ fontWeight: 600 }}>{g.message}</span>
                                <span className="badge" style={{ backgroundColor: '#721c24', color: '#fff', padding: '2px 10px', borderRadius: '999px', fontSize: '0.85em', whiteSpace: 'nowrap' }}>
                                  {g.count} {g.count === 1 ? 'row' : 'rows'}
                                </span>
                              </div>
                              <div style={{ fontSize: '0.85em', color: '#6c757d', marginTop: '4px' }}>
                                {g.rangeLabel}
                              </div>
                            </div>
                          ))}
                        </div>
                        {groups.some(g => g.count > 50) && (
                          <p style={{ fontSize: '0.85em', color: '#6c757d', marginTop: '8px' }}>
                            A large block of rows sharing the exact same error (e.g. every field blank except a serial number) usually means those are unfilled placeholder rows in the spreadsheet, not a real data problem — trimming them out before re-importing is often the fastest fix.
                          </p>
                        )}
                      </div>

                      <details style={{ marginBottom: '20px' }}>
                        <summary style={{ cursor: 'pointer', color: '#495057', marginBottom: '10px' }}>Show every affected row individually</summary>
                        <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', marginTop: '10px' }}>
                          <table className="table" style={{ marginBottom: 0 }}>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                              <tr>
                                <th style={{ width: '80px' }}>Row</th>
                                <th>Email / Identifier</th>
                                <th>Error Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validationResults.errors.map((err, idx) => (
                                <tr key={idx}>
                                  <td>{err.row}</td>
                                  <td>{err.email || '-'}</td>
                                  <td className="text-danger">
                                    {Array.isArray(err.errors) ? (
                                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        {err.errors.map((e, i) => <li key={i}>{e}</li>)}
                                      </ul>
                                    ) : (
                                      err.error
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button className="btn btn-secondary" onClick={() => { setShowValidationModal(false); setFileToken(null); }}>Close</button>
                        {skippableCount > 0 && (
                          <button
                            className="btn btn-primary"
                            title={`Skips the ${skippableCount} blank placeholder row${skippableCount === 1 ? '' : 's'} and imports the ${validCount} valid rows. Rows with other errors are left out and reported back.`}
                            onClick={() => handleConfirmUpload(true)}
                          >
                            Skip {skippableCount} Blank Row{skippableCount === 1 ? '' : 's'} & Import Rest
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {!showValidationModal && (
              <form onSubmit={handleSubmit} className="trainee-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>SN (Serial Number) *</label>
                    <input
                      type="text"
                      value={formData.serial_number}
                      onChange={(e) => {
                        const sn = e.target.value;
                        setFormData({ ...formData, serial_number: sn, tag_number: sn });
                      }}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Tag Number (same as SN)</label>
                    <input
                      type="text"
                      value={formData.tag_number || formData.serial_number}
                      onChange={(e) => setFormData({ ...formData, tag_number: e.target.value })}
                      disabled
                      style={{ opacity: 0.6 }}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Sex</label>
                    <select
                      value={formData.sex}
                      onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Date of Birth (DOB)</label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>PWD</label>
                    <select
                      value={formData.pwd}
                      onChange={(e) => setFormData({ ...formData, pwd: e.target.value })}
                    >
                      <option value="">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>National ID Number (NIN)</label>
                    <input
                      type="text"
                      value={formData.national_id_number}
                      onChange={(e) => setFormData({ ...formData, national_id_number: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Bank Verification Number (BVN)</label>
                    <input
                      type="text"
                      value={formData.bank_verification_number}
                      onChange={(e) => setFormData({ ...formData, bank_verification_number: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Account Number</label>
                    <input
                      type="text"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>State of Residence</label>
                    <input
                      type="text"
                      value={formData.state_of_residence}
                      onChange={(e) => setFormData({ ...formData, state_of_residence: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>LGA of Residence</label>
                    <input
                      type="text"
                      value={formData.lga_of_residence}
                      onChange={(e) => setFormData({ ...formData, lga_of_residence: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Name of Trainee's Parent or Guardian</label>
                  <input
                    type="text"
                    value={formData.parent_guardian_name}
                    onChange={(e) => setFormData({ ...formData, parent_guardian_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address of Trainee's Parent or Guardian</label>
                  <textarea
                    value={formData.parent_guardian_address}
                    onChange={(e) => setFormData({ ...formData, parent_guardian_address: e.target.value })}
                    rows="2"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone Number of Trainee's Parent or Guardian</label>
                    <input
                      type="tel"
                      value={formData.parent_guardian_phone}
                      onChange={(e) => setFormData({ ...formData, parent_guardian_phone: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Education</label>
                    <input
                      type="text"
                      value={formData.education}
                      onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Employment Status</label>
                    <input
                      type="text"
                      value={formData.employment_status}
                      onChange={(e) => setFormData({ ...formData, employment_status: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Completion Status</label>
                    <input
                      type="text"
                      value={formData.completion_status}
                      onChange={(e) => setFormData({ ...formData, completion_status: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Specialization</label>
                    <input
                      type="text"
                      value={formData.specialization}
                      onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Batch</label>
                    <select
                      value={formData.batch_id}
                      onChange={(e) => setFormData({ ...formData, batch_id: e.target.value })}
                    >
                      <option value="">No Batch</option>
                      {Array.isArray(batches) && batches.map(batch => (
                        <option key={batch.id} value={batch.id}>{batch.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditingTrainee(null); }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingTrainee ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {showUploadBatchModal && (
        <div className="modal" onClick={() => { setShowUploadBatchModal(false); setUploadBatchId(''); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Batch for Import</h2>
              <button className="close-btn" onClick={() => { setShowUploadBatchModal(false); setUploadBatchId(''); }}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--hover-bg, #f8f9fa)',
                border: '1px solid var(--border-color, #dee2e6)',
                borderRadius: '6px',
                padding: '14px 16px',
                marginBottom: '18px',
                fontSize: '14px'
              }}>
                <strong>Before you import - please read:</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                  <li>Accepted file types: <strong>.csv, .xlsx, .xls, .ods</strong></li>
                  <li>The first row must be column headers - but they don't have to match any exact wording. After you pick a file, you'll see a "Map Your Columns" screen showing every header the file actually has, with a best guess for what each one means. You can change any of them, or mark a column to be ignored.</li>
                  <li><strong>Serial Number</strong>, <strong>First Name</strong>, and <strong>Last Name</strong> must be mapped to some column before you can continue.</li>
                  <li>Every row needs its own <strong>Serial Number</strong>. If it's blank, repeated elsewhere in the file, or already used by an existing trainee in this batch, that row will be flagged and skipped - it will not be silently imported.</li>
                  <li>Date of Birth can be <code>YYYY-MM-DD</code>, <code>DD/MM/YYYY</code>, or a normal Excel date cell.</li>
                  <li>Nothing is saved until you've reviewed the mapping and the validation report - you'll see a full list of problem rows and the exact reason for each before anything is imported.</li>
                </ul>
                <details style={{ marginTop: '10px' }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--active-color, #0d6efd)' }}>Show all optional columns the template supports</summary>
                  <p style={{ margin: '8px 0 0', color: 'var(--text-secondary, #6c757d)' }}>
                    SEX, DATE OF BIRTH (DOB), PWD, PHONE NUMBER, NATIONAL IDENTIFICATION NUMBER (NIN),
                    BANK VERIFICATION NUMBER (BVN), ACCOUNT NUMBER, BANK NAME, EMAIL, STATE OF RESIDENCE,
                    LGA OF RESIDENCE, NAME OF TRAINEE'S PARENT OR GUARDIAN, ADDRESS OF TRAINEE'S PARENT OR GUARDIAN,
                    PHONE NUMBER OF TRAINEE'S PARENT OR GUARDIAN, EDUCATION, EMPLOYMENT STATUS, COMPLETION STATUS, SPECIALIZATION
                  </p>
                </details>
              </div>
              <p>Please select the batch that will contain the trainees from this import:</p>
              <div className="form-group" style={{ marginTop: '20px' }}>
                <label>Select Batch *</label>
                <select
                  value={uploadBatchId}
                  onChange={(e) => setUploadBatchId(e.target.value)}
                  required
                  style={{ width: '100%', padding: '10px', fontSize: '16px' }}
                >
                  <option value="">-- Select a Batch --</option>
                  {Array.isArray(batches) && batches.map(batch => (
                    <option key={batch.id} value={batch.id}>{batch.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowUploadBatchModal(false); setUploadBatchId(''); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleBatchSelectedForUpload}
                disabled={!uploadBatchId}
              >
                Continue to File Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {showMappingModal && (
        <div className="modal" onClick={handleMappingCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>Map Your Columns</h2>
              <button className="close-btn" onClick={handleMappingCancel}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px' }}>
                Here is every column your file has. We've guessed what each one means below -
                check them over, fix anything that's wrong, and set any column you don't want
                imported to "Ignore this column".
              </p>
              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', marginBottom: '12px' }}>
                <table className="table" style={{ marginBottom: 0 }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                    <tr>
                      <th>Your File's Column</th>
                      <th>Maps To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detectedHeaders.map((header, idx) => (
                      <tr key={idx}>
                        <td>{header ? String(header) : <em style={{ color: '#999' }}>(blank column)</em>}</td>
                        <td>
                          <select
                            value={columnMapping[idx] || ''}
                            onChange={(e) => {
                              const newMapping = [...columnMapping];
                              newMapping[idx] = e.target.value || null;
                              setColumnMapping(newMapping);
                            }}
                            style={{ width: '100%', padding: '6px' }}
                          >
                            <option value="">— Ignore this column —</option>
                            <optgroup label="Required">
                              {fieldDefinitions.filter(f => f.required).map(f => (
                                <option key={f.key} value={f.key}>{f.label}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Optional">
                              {fieldDefinitions.filter(f => !f.required).map(f => (
                                <option key={f.key} value={f.key}>{f.label}</option>
                              ))}
                            </optgroup>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary, #6c757d)' }}>
                * Required: Serial Number, First Name, and Last Name must each be mapped to a column before continuing.
              </p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={handleMappingCancel}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleMappingContinue}
                disabled={uploading || fieldDefinitions.filter(f => f.required).some(f => !columnMapping.includes(f.key))}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm || (() => { })}
        onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      {showExportModal && (
        <div className="modal" onClick={() => { setShowExportModal(false); setExportFields([]); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Export Trainees</h2>
              <button className="close-btn" onClick={() => { setShowExportModal(false); setExportFields([]); }}>
                <FiX />
              </button>
            </div>
            <div className="export-modal-content">
              <p>Select fields to export or export all fields:</p>
              <div className="export-actions" style={{ marginBottom: '20px' }}>
                <button className="btn btn-primary" onClick={() => handleExport(true)}>
                  Export All Fields
                </button>
              </div>
              <div className="export-fields">
                <h3>Select Custom Fields:</h3>
                <div className="fields-grid">
                  {allExportFields.map(field => (
                    <label key={field.key} className="field-checkbox">
                      <input
                        type="checkbox"
                        checked={exportFields.includes(field.key)}
                        onChange={() => toggleExportField(field.key)}
                      />
                      <span>{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowExportModal(false); setExportFields([]); }}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleExport(false)}
                  disabled={exportFields.length === 0 || exporting}
                >
                  {exporting ? 'Exporting...' : `Export Selected (${exportFields.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trainees;

